import React, { useEffect, useRef, useState } from "react";
import { FiArrowUp, FiFile, FiPaperclip, FiX } from "react-icons/fi";

export default function Input({ onSend, isConnecting = false }) {
    const [text, setText] = useState("");
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [isSending, setIsSending] = useState(false);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!file || !file.type.startsWith("image/")) {
            setPreviewUrl("");
            return undefined;
        }

        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const selectFile = (selectedFile) => {
        if (selectedFile) setFile(selectedFile);
    };

    const handlePaste = (event) => {
        const pastedFile = Array.from(event.clipboardData.items)
            .find((item) => item.kind === "file")
            ?.getAsFile();

        if (pastedFile) {
            selectFile(pastedFile);
            event.preventDefault();
        }
    };

    const uploadFile = async (selectedFile) => {
        const formData = new FormData();
        formData.append("file", selectedFile);

        const response = await fetch("https://bartolomeu.azurewebsites.net/api/upload", {
            method: "POST",
            body: formData,
        });

        if (!response.ok) throw new Error("Não foi possível enviar o arquivo.");
        const data = await response.json();
        return data.url;
    };

    const handleSend = async () => {
        if ((!text.trim() && !file) || isSending || isConnecting) return;
        setIsSending(true);

        try {
            const fileUrl = file ? await uploadFile(file) : null;
            await onSend(text.trim(), fileUrl);
            setText("");
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            if (textareaRef.current) textareaRef.current.style.height = "auto";
        } catch (error) {
            console.error("Erro ao enviar mensagem:", error);
        } finally {
            setIsSending(false);
        }
    };

    const handleChange = (event) => {
        setText(event.target.value);
        const textarea = textareaRef.current;
        textarea.style.height = "auto";
        textarea.style.height = `${Math.min(textarea.scrollHeight, 180)}px`;
    };

    return (
        <div className="input-area">
            <div className="composer-wrap">
                <div className="input-box">
                    {file && (
                        <div className="attachment-preview">
                            {previewUrl ? (
                                <img src={previewUrl} alt="Prévia do anexo" />
                            ) : (
                                <div className="attachment-file">
                                    <FiFile />
                                    <span>{file.name}</span>
                                </div>
                            )}
                            <button
                                className="remove-attachment"
                                onClick={() => setFile(null)}
                                aria-label="Remover anexo"
                            >
                                <FiX />
                            </button>
                        </div>
                    )}

                    <div className="composer-row">
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            accept="image/*,.pdf,.doc,.docx,audio/*"
                            onChange={(event) => selectFile(event.target.files[0])}
                        />
                        <button
                            className="icon-button attach-button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isSending || isConnecting}
                            aria-label="Anexar arquivo"
                            title="Anexar arquivo"
                        >
                            <FiPaperclip />
                        </button>
                        <textarea
                            ref={textareaRef}
                            disabled={isSending || isConnecting}
                            placeholder={isConnecting ? "Preparando nova conversa..." : "Envie uma mensagem..."}
                            rows={1}
                            value={text}
                            onChange={handleChange}
                            onPaste={handlePaste}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" && !event.shiftKey) {
                                    event.preventDefault();
                                    handleSend();
                                }
                            }}
                        />
                        <button
                            className="icon-button send-button"
                            onClick={handleSend}
                            disabled={isSending || isConnecting || (!text.trim() && !file)}
                            aria-label="Enviar mensagem"
                        >
                            {isSending ? <span className="button-spinner" /> : <FiArrowUp />}
                        </button>
                    </div>
                </div>
                <p className="input-disclaimer">O Bartolomeu pode cometer erros. Confira informações importantes.</p>
            </div>
        </div>
    );
}
